import React from 'react';
import { Badge, Box, Flex, Heading, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ArrowRight, Buildings } from 'phosphor-react';
import { Link as RouterLink } from 'react-router-dom';

import { GlassCard, GlassPageBg } from '../../components/glass';
import { FloatingAssistant } from '../../components/floatingAssistent';
import { useAuth } from '../../providers/auth';
import type { ISubmoduloHub } from '../../components/sidebar/submodulos';

/**
 * Hub de módulo no padrão do Contábil (`accountingConciliation/index.tsx`):
 * fundo em vidro, cabeçalho do módulo e um cartão por submódulo. Os itens são
 * os mesmos do menu lateral (`components/sidebar/submodulos.tsx`).
 */
interface HubDoModuloProps {
  nome: string;
  descricao: string;
  cor: string;
  icone: React.ReactNode;
  submodulos: ISubmoduloHub[];
}

const CartaoSubmodulo = ({ sub, index }: { sub: ISubmoduloHub; index: number }) => {
  const { Icone } = sub;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 * index, ease: [0.4, 0, 0.2, 1] }}
    >
      <RouterLink
        to={sub.path}
        aria-label={`Abrir ${sub.title}`}
        style={{ display: 'block', height: '100%' }}
      >
        <GlassCard intensity="strong" hoverable p={6} cursor="pointer" h="full">
          <Flex direction="column" gap={4} h="full">
            <Flex align="center" justify="space-between">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="52px"
                h="52px"
                borderRadius="2xl"
                bg={sub.tint}
                flexShrink={0}
              >
                <Icone size={26} color={sub.cor} />
              </Box>
              <Flex align="center" gap={2}>
                {sub.badge && (
                  <Badge colorScheme="blue" borderRadius="full" px={2} fontSize="9px">
                    {sub.badge}
                  </Badge>
                )}
                <Box opacity={0.5}>
                  <ArrowRight size={18} color="#1B88F5" weight="bold" />
                </Box>
              </Flex>
            </Flex>
            <Box flex={1}>
              <Heading
                as="h3"
                fontSize="md"
                fontWeight="600"
                color="#0F172A"
                lineHeight="1.3"
                mb={1}
              >
                {sub.title}
              </Heading>
              <Text fontSize="xs" color="#71717A">
                {sub.descricao}
              </Text>
            </Box>
          </Flex>
        </GlassCard>
      </RouterLink>
    </motion.div>
  );
};

export const HubDoModulo = ({ nome, descricao, cor, icone, submodulos }: HubDoModuloProps) => {
  const { hasPermission, isAdmin } = useAuth();
  const visiveis = submodulos.filter(s => isAdmin() || hasPermission(s.path));

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" gap={5} mb={3}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="64px"
              h="64px"
              borderRadius="2xl"
              bg={cor}
              boxShadow="0 8px 24px -4px rgba(27, 136, 245, 0.2)"
            >
              {icone}
            </Box>
            <Box>
              <Text
                fontSize="11px"
                color="#94A3B8"
                textTransform="uppercase"
                letterSpacing="0.1em"
                fontWeight="semibold"
              >
                Módulo
              </Text>
              <Heading
                fontSize={{ base: '24px', md: '32px' }}
                fontWeight="700"
                color="#0F172A"
                lineHeight="1.1"
                letterSpacing="-0.02em"
              >
                {nome}
              </Heading>
            </Box>
          </Flex>
          <Text fontSize="sm" color="#71717A" mb={10} ml={1} maxW="640px" lineHeight="1.6">
            {descricao}
          </Text>
        </motion.div>

        <Flex align="center" gap={2} mb={5}>
          <Buildings size={18} color="#1B88F5" weight="duotone" />
          <Heading as="h2" fontSize="md" fontWeight="600" color="#0F172A" letterSpacing="-0.01em">
            Submódulos disponíveis
          </Heading>
          <Text fontSize="xs" color="#94A3B8" ml={1}>
            ({visiveis.length})
          </Text>
        </Flex>

        {visiveis.length === 0 ? (
          <GlassCard intensity="medium" p={10} textAlign="center">
            <Stack align="center" spacing={2}>
              <Text fontSize="md" color="#71717A">
                Você não tem acesso a nenhum submódulo desta seção.
              </Text>
            </Stack>
          </GlassCard>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
            {visiveis.map((sub, idx) => (
              <CartaoSubmodulo key={sub.path} sub={sub} index={idx} />
            ))}
          </SimpleGrid>
        )}
      </Box>
      <FloatingAssistant />
    </GlassPageBg>
  );
};
